import { useEffect, useState } from 'react';

export const debounce = <F extends (...args: Parameters<F>) => void>(
  func: F,
  wait: number,
  immediate: boolean = false
): ((...args: Parameters<F>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    if (immediate && !timeout) {
      func(...args);
    }

    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) {
        func(...args);
      }
    }, wait);
  };
};

export const useDebounce = <T>(
  state: T,
  callback: (state: T) => void,
  duration = 500
) => {
  const [debouncedState, setDebouncedState] = useState(state);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedState(state);
      callback(state);
    }, duration);
    return () => clearTimeout(timeout);
  }, [state]);

  return debouncedState;
};
